import { createElement, Fragment, memo, MouseEventHandler, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';

import Menu from '@mui/material/Menu';

import css from '../SideBar.module.css';

import { isUserAdminAtom } from 'v2/atoms/userAtom';
import { UserRole } from 'v2/constants';
import { NavigationParent, Nullable } from 'v2/types';
import { isRouteAllowed } from 'v2/utils';

interface FadedMenuItemProps {
  navigationItem: NavigationParent;
  userRoles: UserRole[];
}

export const FadedMenuItem = memo<FadedMenuItemProps>(({ navigationItem, userRoles }) => {
  const location = useLocation();

  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const isUserAdmin = useAtomValue(isUserAdminAtom);

  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const isAtLeastOneAllowed = useMemo(() => {
    if (isUserAdmin) {
      return true;
    }

    return isRouteAllowed(userRoles, navigationItem.group);
  }, [navigationItem, isUserAdmin]);

  const isActive = useMemo(() => {
    return !!navigationItem.children?.some((c) => location.pathname.includes(c.path));
  }, [navigationItem, location]);

  const onMouseEnterTrigger: MouseEventHandler<HTMLElement> = (e) => {
    clearTimeout(timerRef.current);
    setAnchorEl(e.currentTarget);
  };

  const onMouseLeaveTrigger = () => {
    timerRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  const onMouseEnterMenu = () => {
    clearTimeout(timerRef.current);
  };

  const onMouseLeaveMenu = () => {
    setAnchorEl(null);
  };

  return (
    isAtLeastOneAllowed && (
      <Fragment>
        <button
          key={navigationItem.title}
          className={clsx(css.sideBarItem, isActive && css.active)}
          onMouseEnter={onMouseEnterTrigger}
          onMouseLeave={onMouseLeaveTrigger}
        >
          {createElement(navigationItem.icon, { className: css.sideBarItemIcon })}
        </button>

        <Menu
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
          slotProps={{
            list: { onMouseEnter: onMouseEnterMenu, onMouseLeave: onMouseLeaveMenu },
            paper: { className: css.sideBarItemMenu },
            root: { className: css.sideBarItemMenuBackdrop },
          }}
          hideBackdrop
        >
          <div className={css.sideBarPopoverMenu}>
            {navigationItem.children?.map(
              (c) =>
                isRouteAllowed(userRoles, c.group) && (
                  <NavLink
                    key={c.title}
                    className={({ isActive }) => clsx(css.sideBarItem, css.asMenuItem, isActive && css.active)}
                    to={c.path ?? ''}
                  >
                    {c.title}
                  </NavLink>
                )
            )}
          </div>
        </Menu>
      </Fragment>
    )
  );
});
